import React from 'react';

interface LocationMapProps {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  address: string | undefined;
  className?: string;
}

/**
 * LocationMap component - displays a map with the shipment location
 * 
 * Note: This is a placeholder implementation that would be replaced with a real
 * map integration (Google Maps, Mapbox, etc.) in production
 */
const LocationMap: React.FC<LocationMapProps> = ({
  latitude,
  longitude,
  address,
  className = 'h-64'
}) => {
  // Check if we have valid coordinates
  const hasValidCoordinates = 
    latitude !== undefined && 
    latitude !== null && 
    longitude !== undefined && 
    longitude !== null;

  return (
    <div className="space-y-2">
      <div 
        className={`${className} bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-white/10 relative ${hasValidCoordinates ? '' : 'flex items-center justify-center'}`}
      >
        {hasValidCoordinates ? (
          <>
            {/* This is a placeholder for a real map - would be replaced with a map library */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800"></div>
            
            {/* Fake map grid lines */}
            <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className="border border-blue-300 dark:border-blue-700 opacity-30"></div>
              ))}
            </div>
            
            {/* Center marker */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="h-6 w-6 bg-red-500 rounded-full border-2 border-white shadow-lg"></div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-2 bg-red-500 rounded-b-full"></div>
            </div>
            
            {/* Coordinates display */}
            <div className="absolute bottom-2 right-2 text-xs bg-white bg-opacity-80 dark:bg-slate-800 dark:bg-opacity-80 dark:text-gray-200 rounded px-2 py-1">
              {latitude.toFixed(4)}, {longitude.toFixed(4)}
            </div>
          </>
        ) : (
          <div className="text-gray-500 dark:text-gray-400">
            No location coordinates available
          </div>
        )}
      </div>
      
      {/* Address display below map */}
      {address ? (
        <div className="text-gray-600 dark:text-gray-400 text-sm">
          {address}
        </div>
      ) : (
        <div className="text-gray-500 dark:text-gray-400 text-sm italic">
          No address information available
        </div>
      )}
      
      {/* Note about real implementation */}
      <div className="text-xs text-gray-400 italic">
        Note: In production, this would be replaced with an interactive map using Google Maps or similar.
      </div>
    </div>
  );
};

export default LocationMap;